import React, { useState } from 'react';
import Sidebanner from './sidebanner';
import { Helmet } from 'react-helmet-async';

const FAQ_DATA = [
  {
    question: "When will my page appear on Google?",
    answer: "Google's discovery process typically takes 24–72 hours after your page is live. However, full indexing can take 7–14 days. We automatically submit your sitemap to speed this up, but Google’s internal crawl budget determines the final timeline."
  },
{
  category: "Review Integrity",
  question: "How does the review verification process work?",
  answer: "We employ a high-security, dual-path verification system. 1) Invited Customers: Users invited via email are pre-verified through a unique 'Identity Key.' While they aren't required to upload manual proof, every submission still undergoes a background security audit (IP integrity and metadata checks) before approval. 2) Public Users: To maintain our 'Zero-Fraud' standard, non-invited users must upload 'Proof of Service' (receipts/documentation) for manual audit by our team before their review can be indexed on Google."
},
{
  category: "User Experience",
  question: "Why can some users leave reviews without logging in?",
  answer: "Our 'Frictionless Protocol' uses secure, one-time-use email tokens. When a customer is invited by a business, the identity is pre-authenticated. This allows the customer to submit their feedback in one click without the friction of a traditional login process, resulting in a 4x higher conversion rate for your business."
},
  {
  category: "Features",
  question: "What is the 'Message-to-Review' system?",
  answer: "It allows you to take screenshots of positive feedback from WhatsApp, Instagram, or iMessage and convert them into structured, SEO-ready reviews. This ensures the praise you receive in private conversations helps your Google ranking."
},
  {
    question: "When will my 'Stars' (Rich Snippets) show up in search results?",
    answer: "Google usually requires a 'Trust Phase' of 2–4 weeks for new domains or pages. Even if your schema is perfect (which it is), Google audits the data integrity before displaying Aggregate Rating stars. Patience is a sign of a high-quality index. But keep in mind Rich Snippet appearance is never guaranteed."
  },
  {
  category: "Account & Billing",
  question: "What happens if I stop paying for my subscription?",
  answer: "Your public business page remains live and accessible. However, your 'Conversion Tools' will be locked: the Website Widget will disappear from your site, and you will lose the ability to use the 'Message-to-Review' feature (converting customer screenshots into verified reviews). Manual public reviews will still function."
},
  {
    question: "Can I change my business address or logo?",
    answer: "Yes. You can update these in Settings at any time. Because your Sitemanifest ID is permanent, Google will simply update your existing search result without losing your reviews or rankings."
  },
  {
    question: "How do I speed up my indexation?",
    answer: "Ensure you have linked your Sitemanifest profile from your main website or social media. External links are the 'roads' Googlebot uses to find your new Identity Anchor."
  },
];

export default function BusinessFaqs() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    // Calculating space: mr-[250px] for small xl, mr-[350px] for large xl to clear the sidebanner
    <div className="min-h-screen bg-[#F9FAFB] lg:mr-[250px] xl:mr-[350px] pt-[80px] lg:pt-[100px]  xl:pt-[120px] p-[20px] xl:p-[60px] font-poppins">
            <Helmet>
                    <title>Business Faqs | Sitemanifest</title> 
                    
                    <meta name="robots" content="noindex, nofollow" />
                    
                    <meta name="googlebot" content="noarchive" />
                  </Helmet>
                  
      <div className="max-w-[800px] mx-auto">
        <header className="mb-[40px]">
          <h1 className="text-[28px] xl:text-[36px] font-bold text-[#323232] tracking-tight">
            Business FAQs
          </h1>
          <p className="text-[#666] mt-[10px] text-[15px] xl:text-[17px]">
            Everything you need to know about your digital identity and Google performance.
          </p>
        </header>

        <div className="space-y-[15px]">
          {FAQ_DATA.map((faq, index) => (
            <div 
              key={index} 
              className="bg-white border border-[#E5E7EB] rounded-[12px] overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-[20px] text-left hover:bg-[#F3F4F6] transition-colors"
              >
                <span className="font-semibold text-[#323232] text-[16px] xl:text-[18px]">
                  {faq.question}
                </span>
                <span className={`text-[24px] transform transition-transform ${activeIndex === index ? 'rotate-45' : 'rotate-0'}`}>
                  +
                </span>
              </button>
              
              <div 
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  activeIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-[20px] pt-2 text-[#555] leading-relaxed border-t border-[#F3F4F6] text-[14px] xl:text-[16px]">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <footer className="mt-[60px] p-[30px] bg-[#32596E] rounded-[16px] text-white">
          <h3 className="text-[20px] font-bold">Still have questions?</h3>
          <p className="opacity-80 mt-[5px]">Our technical team is monitoring your schema 24/7.</p>
          <button className="mt-[20px] bg-white text-[#32596E] px-[24px] py-[10px] rounded-[6px] font-bold hover:bg-opacity-90 transition-all">
            Contact Support
          </button>
        </footer>
      </div>
      <Sidebanner />
    </div>
  );
}